import type {
  TaskDetail,
  TaskLog,
  TaskLogKindEnum,
  TaskStatus,
  TaskStatusColorEnum,
  TaskSummary,
} from '~/data/generated/model';

export type TaskLogKind = TaskLogKindEnum;
export type TaskCategory =
  | 'general'
  | 'invoice_dispute'
  | 'maintenance'
  | 'compliance'
  | 'contract'
  | 'performance'
  | 'safety'
  | 'commitment_follow_up';
export type CreatedRange = 'any' | 'd7' | 'd30' | 'd90' | 'year' | 'custom';

export const STATUS_COLOR: Record<
  TaskStatusColorEnum,
  { bg: string; fg: string; border: string; solid: string }
> = {
  blue: { bg: '#e3ecfb', fg: '#1f4fb0', border: '#b9cdf2', solid: '#2d5fc4' },
  amber: { bg: '#fcefd6', fg: '#8a5a00', border: '#f0cf8f', solid: '#d99a1e' },
  green: { bg: '#e0f3ec', fg: '#1e6b4f', border: '#a9dcc8', solid: '#2e8b62' },
  grey: { bg: '#eceff3', fg: '#46505c', border: '#cfd6e0', solid: '#7b8794' },
  red: { bg: '#fde4e1', fg: '#b3261e', border: '#f3b8b1', solid: '#d0453b' },
  purple: { bg: '#efe6fa', fg: '#6a3fa0', border: '#d4c0ef', solid: '#8a5cc7' },
  teal: { bg: '#dff4f4', fg: '#1d6a6a', border: '#a6dada', solid: '#2b9393' },
};

export const STATUS_COLORS = Object.keys(STATUS_COLOR) as TaskStatusColorEnum[];

/** Statuses run in the order the company set. */
export function orderStatuses<T extends Pick<TaskStatus, 'position'>>(statuses: readonly T[]): T[] {
  return [...statuses].sort((a, b) => a.position - b.position);
}

export function isClosedStatus(status: Pick<TaskStatus, 'kind'>): boolean {
  return status.kind !== 'open';
}

/** Completing a task asks what to remember, unless it was published with no updates since. */
export function asksForKnowledge(
  task: Pick<TaskDetail, 'status' | 'publishedAt' | 'substantiveLogCount' | 'publishedLogCount'>,
  from: Pick<TaskStatus, 'kind'>,
): boolean {
  if (task.status.kind !== 'completed' || isClosedStatus(from)) return false;
  return !task.publishedAt || task.substantiveLogCount > task.publishedLogCount;
}

/** A closed task has nothing left to follow up on. */
export function isFollowUpOverdue(
  task: Pick<TaskSummary, 'followUpDate' | 'status'>,
  today: Date = new Date(),
): boolean {
  return (
    task.followUpDate !== null && !isClosedStatus(task.status) && task.followUpDate < isoDate(today)
  );
}

export function isoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export const QUICK_FOLLOW_UPS: [label: string, days: number][] = [
  ['Tomorrow', 1],
  ['In 3 days', 3],
  ['Next week', 7],
];

export function isoDaysFromToday(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return isoDate(d);
}

export const LOG_KIND: Record<TaskLogKind, { label: string; bg: string; fg: string }> = {
  issue_opened: { label: 'Issue opened', bg: '#e0f3ec', fg: '#1e6b4f' },
  update: { label: 'Update', bg: '#eceef1', fg: '#4a525c' },
  chase_sent: { label: 'Chase sent', bg: '#fcefd6', fg: '#8a5a00' },
  provider_update: { label: 'Provider update', bg: '#eceef1', fg: '#4a525c' },
  scope_change: { label: 'Scope change', bg: '#e3ecfb', fg: '#2350a8' },
  escalated: { label: 'Escalated', bg: '#fde4e1', fg: '#b3261e' },
  task_complete: { label: 'Task complete', bg: '#e0f3ec', fg: '#1e6b4f' },
  resolved: { label: 'Resolved', bg: '#e0f3ec', fg: '#1e6b4f' },
  status_changed: { label: 'Status', bg: '#e3ecfb', fg: '#2350a8' },
  assignee_changed: { label: 'Owner', bg: '#f1f3f6', fg: '#5f6b78' },
  elevators_changed: { label: 'Elevators', bg: '#f1f3f6', fg: '#5f6b78' },
  follow_up_changed: { label: 'Follow-up', bg: '#f1f3f6', fg: '#5f6b78' },
  title_changed: { label: 'Title', bg: '#f1f3f6', fg: '#5f6b78' },
  description_changed: { label: 'Description', bg: '#f1f3f6', fg: '#5f6b78' },
  category_changed: { label: 'Category', bg: '#f1f3f6', fg: '#5f6b78' },
  evidence_added: { label: 'Evidence', bg: '#f1f3f6', fg: '#5f6b78' },
  published: { label: 'Knowledge', bg: '#f1f3f6', fg: '#5f6b78' },
};

const SYSTEM_LOG_KINDS: ReadonlySet<TaskLogKind> = new Set([
  'assignee_changed',
  'elevators_changed',
  'follow_up_changed',
  'title_changed',
  'description_changed',
  'category_changed',
  'evidence_added',
  'published',
]);

export function humanLogs(task: TaskDetail): TaskLog[] {
  return task.logs.filter((log) => !SYSTEM_LOG_KINDS.has(log.kind));
}

export const CATEGORY_LABEL: Record<TaskCategory, string> = {
  general: 'General',
  invoice_dispute: 'Invoice dispute',
  maintenance: 'Maintenance',
  compliance: 'Compliance',
  contract: 'Contract',
  performance: 'Performance',
  safety: 'Safety',
  commitment_follow_up: 'Commitment follow-up',
};

export const KNOWLEDGE_KIND_STYLE: Record<
  'note' | 'decision' | 'commitment',
  { bg: string; fg: string; border: string }
> = {
  note: { bg: '#fff', fg: '#1b1f24', border: '#555' },
  decision: { bg: '#e3ecfb', fg: '#1f4fb0', border: '#b9cdf2' },
  commitment: { bg: '#fcefd6', fg: '#8a5a00', border: '#f0cf8f' },
};

export function initials(name: string | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

const AVATAR_COLORS = ['#1e6b4f', '#2d5fc4', '#8e44ad', '#b5561b', '#1a3f8f'];

export function avatarColor(id: string | undefined): string {
  if (!id) return '#9aa3ad';
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

export const CREATED_RANGES: { key: Exclude<CreatedRange, 'custom'>; label: string }[] = [
  { key: 'any', label: 'Any time' },
  { key: 'd7', label: 'Last 7 days' },
  { key: 'd30', label: 'Last 30 days' },
  { key: 'd90', label: 'Last 90 days' },
  { key: 'year', label: 'This year' },
];

const DAYS: Record<string, number> = { d7: 7, d30: 30, d90: 90 };

export function inCreatedRange(
  createdAt: string,
  range: CreatedRange,
  from?: string,
  to?: string,
  today: Date = new Date(),
): boolean {
  const created = new Date(createdAt);
  if (range === 'custom') {
    return (
      (!from || created >= new Date(`${from}T00:00`)) &&
      (!to || created <= new Date(`${to}T23:59:59.999`))
    );
  }
  if (range === 'any') return true;
  if (range === 'year') return created.getFullYear() === today.getFullYear();
  return (today.getTime() - created.getTime()) / 864e5 <= DAYS[range];
}

function toDate(value: string): Date {
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00` : value);
}

export function formatShortDate(value: string): string {
  return toDate(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatDate(value: string): string {
  return toDate(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
